import type { HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import { Typography, type TypographyTone } from '../../typography';
import styles from './form-hint.module.scss';

export type FormHintVariant = 'info' | 'important' | 'error' | 'primary';

const textToneByVariant: Record<FormHintVariant, TypographyTone> = {
  info: 'md',
  error: 'danger-dk',
  important: 'background',
  primary: 'primary-dk',
};

type FormHintSize = 'small' | 'medium';

interface FormHintProps extends Omit<HTMLAttributes<HTMLDivElement>, 'prefix'> {
  children: ReactNode;
  prefix?: ReactNode;
  size?: FormHintSize;
  variant?: FormHintVariant;
  withBorderRadius?: boolean;
}

export function FormHint({ children, className, prefix, size = 'small', variant = 'info', withBorderRadius = true, ...rest }: FormHintProps) {
  return (
    <div
      className={clsx(
        styles.formHint,
        withBorderRadius && styles.withBorderRadius,
        prefix ? styles.hasPrefix : undefined,
        styles[`variant-${variant}`],
        styles[`size-${size}`],
        className,
      )}
      {...rest}
    >
      <Typography inheritance="current" lineHeight="1.125rem" size="0.875rem" tone={textToneByVariant[variant]} wrap="preserve">
        <HorizontalStack gap={2} wrap="nowrap">
          {prefix}
          {children}
        </HorizontalStack>
      </Typography>
    </div>
  );
}
